import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  clientStatusSchema,
  JURISDICTIONS,
} from '@1financial/shared';
import type {
  ClientOutput,
  UpdateClientInput,
} from '@1financial/shared';
import { useAuth, getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { downloadFromApi } from '../lib/download.js';
import { Flag } from '../components/Flag.js';
import { TagCheckboxes } from '../components/TagCheckboxes.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { BulkActionBar, BulkActionButton, BulkResultToast, postBulk, StaffSelect, type BulkResult } from '../components/BulkActionBar.js';
import { SortableTh, TablePager, useTableControls } from '../components/TableControls.js';
import { FilterBar, EmptyFilterRow, type ActiveFilter } from '../components/FilterBar.js';
import { ApplyTemplateModal } from '../components/ApplyTemplateModal.js';
import { OnboardingWizard } from '../components/OnboardingWizard.js';
import { CompaniesHouseOnboardingWizard } from '../components/CompaniesHouseOnboardingWizard.js';
import { ClientFormModal } from '../components/ClientFormModal.js';
import { TableSkeletonRows } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { ViewIcon, EditIcon, DeleteIcon } from '../components/ActionIcons.js';
import { Users } from 'lucide-react';
import { useEscape } from '../hooks/useEscape.js';
import { useUserPreference } from '../lib/user-preferences.js';

export function ClientsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useUserPreference('clients.status', '');
  const [country, setCountry] = useUserPreference('clients.country', '');
  const [jurisdiction, setJurisdiction] = useUserPreference('clients.jurisdiction', '');
  const [tagId, setTagId] = useUserPreference('clients.tagId', '');
  const [showNewClient, setShowNewClient] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('new') === '1';
  });
  const [showImport, setShowImport] = useState(false);
  const [onboardResumeId, setOnboardResumeId] = useState<string | null>(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('onboard');
  });
  const [showOnboarding, setShowOnboarding] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('onboard') !== null;
  });
  const [showLinkWizard, setShowLinkWizard] = useState(false);
  const [editing, setEditing] = useState<ClientOutput | null>(null);
  const [deleting, setDeleting] = useState<ClientOutput | null>(null);
  const [applyTemplateFor, setApplyTemplateFor] = useState<string | null>(null);

  const { canDo } = useCapabilities();
  const canBulkTags = canDo('tags.manage');
  const canBulkEdit = canDo('clients.edit');
  const canBulkArchive = canDo('clients.bulk_delete');
  const canApplyTemplate = canDo('templates.view') && canDo('compliance.edit');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<'addTags' | 'removeTags' | 'setOwner' | 'archive' | null>(null);
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const { sortBy, sortOrder, cursor, pageIndex, toggleSort, goNext, goPrev } = useTableControls('updatedAt', 'desc', [search, status, country, jurisdiction, tagId]);

  // Selection only makes sense for the rows currently on screen.
  useEffect(() => { setSelected(new Set()); }, [search, status, country, jurisdiction, tagId, sortBy, sortOrder, cursor]);

  const facets = useQuery({
    queryKey: ['client-facets'],
    enabled: api !== null,
    queryFn: async () => {
      const res = await fetch('/v1/clients/facets', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { countries: string[]; jurisdictions: string[]; jurisdictionCounts: Record<string, number> } };
      return body.data;
    },
  });

  const tagOptions = useQuery({
    queryKey: ['tags'],
    enabled: api !== null,
    queryFn: async () => {
      const res = await fetch('/v1/tags?limit=100', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: { id: string; name: string }[] } };
      return body.data.items;
    },
  });

  const activeFilters: ActiveFilter[] = [
    ...(search ? [{ key: 'search', label: `Search: ${search}` }] : []),
    ...(status ? [{ key: 'status', label: `Status: ${status}` }] : []),
    ...(country ? [{ key: 'country', label: `Country: ${country}` }] : []),
    ...(jurisdiction ? [{ key: 'jurisdiction', label: `Jurisdiction: ${jurisdiction}` }] : []),
    ...(tagId ? [{ key: 'tagId', label: `Tag: ${tagOptions.data?.find((t) => t.id === tagId)?.name ?? tagId.slice(0, 8)}` }] : []),
  ];

  function removeFilter(key: string) {
    if (key === 'search') setSearch('');
    else if (key === 'status') setStatus('');
    else if (key === 'country') setCountry('');
    else if (key === 'jurisdiction') setJurisdiction('');
    else if (key === 'tagId') setTagId('');
  }

  function clearAllFilters() {
    setSearch('');
    setStatus('');
    setCountry('');
    setJurisdiction('');
    setTagId('');
  }

  function openNewClient() {
    setShowNewClient(true);
  }

  function exportClients() {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    if (status) p.set('status', status);
    if (country) p.set('country', country);
    if (jurisdiction) p.set('jurisdiction', jurisdiction);
    if (tagId) p.set('tagId', tagId);
    p.set('all', 'true');
    downloadFromApi(`/v1/clients/export?${p.toString()}`, 'clients.csv')
      .catch((e) => alert(e instanceof Error ? e.message : 'Export failed'));
  }
  function closeNewClient() {
    setShowNewClient(false);
    const url = new URL(window.location.href);
    url.searchParams.delete('new');
    window.history.replaceState({}, '', url.pathname + url.search);
  }

  const list = useQuery({
    queryKey: ['clients', { search, status, country, jurisdiction, tagId, sortBy, sortOrder, cursor }],
    enabled: api !== null,
    queryFn: async () => {
      if (!api) throw new Error('not authenticated');
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      if (country) params.set('country', country);
      if (jurisdiction) params.set('jurisdiction', jurisdiction);
      if (tagId) params.set('tagId', tagId);
      params.set('all', 'true');
      params.set('limit', '50');
      params.set('sortBy', sortBy);
      params.set('sortOrder', sortOrder);
      if (cursor) params.set('cursor', cursor);
      const res = await fetch(`/v1/clients?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: ClientOutput[]; total: number; nextCursor: string | null } };
      return body.data;
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/clients/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setDeleting(null);
    },
  });

  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: UpdateClientInput }) => {
      const res = await fetch(`/v1/clients/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
        },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setEditing(null);
    },
  });

  async function runBulk(payload: { action: string; params?: Record<string, unknown> }) {
    setBulkBusy(true);
    try {
      const result = await postBulk('/v1/clients/bulk', { ...payload, ids: [...selected] });
      setBulkResult(result);
      setBulkAction(null);
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      void queryClient.invalidateQueries({ queryKey: ['entity-tags'] });
    } catch (err) {
      setBulkResult({
        updated: 0,
        failed: [...selected].map((id) => ({ id, error: err instanceof Error ? err.message : 'Bulk request failed' })),
      });
    } finally {
      setBulkBusy(false);
    }
  }

  const visibleIds = (list.data?.items ?? []).map((c) => c.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  function toggleAllVisible() {
    setSelected(allVisibleSelected ? new Set() : new Set(visibleIds));
  }

  function toggleSelected(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Clients</h1>
          <p className="text-sm text-muted">{list.data?.total ?? '—'} active</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportClients}
            className="text-sm text-muted hover:text-white border border-border hover:border-accent px-3 py-1.5 rounded-lg"
          >
            Export
          </button>
          <button
            disabled={!canDo('clients.create')}
            onClick={() => setShowImport(true)}
            className="text-sm text-muted hover:text-white border border-border hover:border-accent px-3 py-1.5 rounded-lg"
          >
            Import
          </button>
          <button
            disabled={!canDo('clients.create')}
            onClick={openNewClient}
            className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg"
          >
            + New client
          </button>
          {canDo('clients.create') && (
            <button
              onClick={() => setShowOnboarding(true)}
              className="text-sm text-accent hover:text-white border border-accent/60 hover:border-accent px-3 py-1.5 rounded-lg"
            >
              New from document
            </button>
          )}
          {canDo('clients.create') && <button onClick={() => setShowLinkWizard(true)} className="text-sm text-accent hover:text-white border border-accent/60 hover:border-accent px-3 py-1.5 rounded-lg">Create from link</button>}
        </div>
      </header>

      <FilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name, company, email…"
        activeFilters={activeFilters}
        onRemoveFilter={removeFilter}
        onClearAll={clearAllFilters}
      >
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
        >
          <option value="">All statuses</option>
          {clientStatusSchema.options.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
        >
          <option value="">All countries</option>
          {(facets.data?.countries ?? []).map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          value={jurisdiction}
          onChange={(e) => setJurisdiction(e.target.value)}
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
        >
          <option value="">All jurisdictions</option>
          {(facets.data?.jurisdictions ?? []).map((j) => (
            <option key={j} value={j}>{j}{facets.data?.jurisdictionCounts?.[j] != null ? ` (${facets.data.jurisdictionCounts[j]})` : ''}</option>
          ))}
        </select>
        <select
          value={tagId}
          onChange={(e) => setTagId(e.target.value)}
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
        >
          <option value="">All tags</option>
          {(tagOptions.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </FilterBar>

      <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left text-xs text-muted bg-bg-card border-b border-border">
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  aria-label="Select all visible"
                  className="w-4 h-4 accent-emerald-500 cursor-pointer align-middle"
                />
              </th>
              <SortableTh label="Company" sortKey="company" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <SortableTh label="Name" sortKey="name" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <th className="px-4 py-3 font-medium">Tags</th>
              <th className="px-4 py-3 font-medium">Contact</th>
              <SortableTh label="Country" sortKey="country" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <SortableTh label="Status" sortKey="status" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <TableSkeletonRows colSpan={8} />
            )}
            {list.error && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-rose-400">
                {(list.error as Error).message}
              </td></tr>
            )}
            {list.data?.items.length === 0 && (
              activeFilters.length > 0
                ? <EmptyFilterRow colSpan={8} onClearAll={clearAllFilters} />
                : <tr><td colSpan={8}><EmptyState icon={Users} title="No clients yet." /></td></tr>
            )}
            {list.data?.items.map((c) => (
              <tr
                key={c.id}
                className="border-b border-border/50 hover:bg-bg-hover/40"
              >
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => toggleSelected(c.id)}
                    aria-label={`Select ${c.name}`}
                    className="w-4 h-4 accent-emerald-500 cursor-pointer align-middle"
                  />
                </td>
                <td className="px-4 py-3 text-white">{c.company ?? '—'}</td>
                <td className="px-4 py-3 text-muted">{c.name}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-1 max-w-[220px]">
                    {c.tags.slice(0, 3).map((t) => (
                      <span
                        key={t.id}
                        className="inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium whitespace-nowrap"
                        style={{ borderColor: t.color, color: t.color, backgroundColor: `${t.color}1a` }}
                      >
                        {t.name}
                      </span>
                    ))}
                    {c.tags.length > 3 && (
                      <span className="text-xs text-muted">+{c.tags.length - 3}</span>
                    )}
                    {c.tags.length === 0 && <span className="text-muted">—</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-muted">{c.email ?? '—'}</td>
                <td className="px-4 py-3">
                  {c.country && <Flag country={c.country} className="mr-1.5" />}
                  <span className="text-xs text-muted">{c.country ?? '—'}</span>
                </td>
                <td className="px-4 py-3">
                  <StatusPill status={c.status} />
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <ViewIcon to={`/clients/${c.id}`} label="View client" />
                    <EditIcon onClick={() => setEditing(c)} label="Edit client" />
                    <DeleteIcon onClick={() => setDeleting(c)} label="Delete client" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePager
          pageIndex={pageIndex}
          count={list.data?.items.length ?? 0}
          limit={50}
          total={list.data?.total}
          hasNext={Boolean(list.data?.nextCursor)}
          onPrev={goPrev}
          onNext={() => goNext(list.data?.nextCursor)}
        />
      </div>

      {showNewClient && (
        <ClientFormModal
          mode="create"
          onClose={closeNewClient}
          onCreated={(id) => {
            closeNewClient();
            queryClient.invalidateQueries({ queryKey: ['clients'] });
            if (canApplyTemplate) {
              setApplyTemplateFor(id);
            } else {
              navigate(`/clients/${id}`);
            }
          }}
        />
      )}

      {applyTemplateFor && (
        <ApplyTemplateModal
          clientId={applyTemplateFor}
          skipLabel="Skip"
          onClose={() => {
            const id = applyTemplateFor;
            setApplyTemplateFor(null);
            navigate(`/clients/${id}`);
          }}
          onDone={() => {
            const id = applyTemplateFor;
            setApplyTemplateFor(null);
            queryClient.invalidateQueries({ queryKey: ['compliance-items'] });
            navigate(`/clients/${id}`);
          }}
        />
      )}

      {showImport && (
        <ImportClientsModal
          onClose={() => setShowImport(false)}
          onSuccess={() => {
            setShowImport(false);
            queryClient.invalidateQueries({ queryKey: ['clients'] });
          }}
        />
      )}

      {showLinkWizard && <CompaniesHouseOnboardingWizard onClose={() => setShowLinkWizard(false)} onCreated={() => { queryClient.invalidateQueries({ queryKey: ['clients'] }); }} onDone={(id) => { setShowLinkWizard(false); queryClient.invalidateQueries({ queryKey: ['clients'] }); navigate(`/clients/${id}`); }} />}

      {showOnboarding && (
        <OnboardingWizard
          resumeId={onboardResumeId}
          onClose={() => {
            setShowOnboarding(false);
            setOnboardResumeId(null);
            const url = new URL(window.location.href);
            url.searchParams.delete('onboard');
            window.history.replaceState({}, '', url.pathname + url.search);
          }}
          onSuccess={(id) => {
            setShowOnboarding(false);
            setOnboardResumeId(null);
            const url = new URL(window.location.href);
            url.searchParams.delete('onboard');
            window.history.replaceState({}, '', url.pathname + url.search);
            queryClient.invalidateQueries({ queryKey: ['clients'] });
            queryClient.invalidateQueries({ queryKey: ['compliance-items'] });
            navigate(`/clients/${id}`);
          }}
        />
      )}

      {editing && (
        <ClientFormModal
          mode="edit"
          client={editing}
          onClose={() => setEditing(null)}
          onSubmit={(patch) => update.mutate({ id: editing.id, patch })}
          error={update.error?.message ?? null}
          submitting={update.isPending}
        />
      )}

      {deleting && (
        <ConfirmModal
          title="Delete client"
          message={`Delete ${deleting.name}? The row is soft-deleted — it disappears from lists but can be restored (or permanently deleted) from Admin → Trash.`}
          onClose={() => setDeleting(null)}
          onConfirm={() => remove.mutate(deleting.id)}
          error={remove.error?.message ?? null}
          submitting={remove.isPending}
          confirmText="Delete"
          confirmClass="bg-rose-700 hover:bg-rose-600 text-oncolor"
        />
      )}

      {selected.size > 0 && (canBulkTags || canBulkEdit || canBulkArchive) && (
        <BulkActionBar count={selected.size} onClear={() => setSelected(new Set())}>
          {canBulkTags && <BulkActionButton onClick={() => setBulkAction('addTags')}>Add tags</BulkActionButton>}
          {canBulkTags && <BulkActionButton onClick={() => setBulkAction('removeTags')}>Remove tags</BulkActionButton>}
          {canBulkEdit && <BulkActionButton onClick={() => setBulkAction('setOwner')}>Set owner</BulkActionButton>}
          {canBulkArchive && <BulkActionButton danger onClick={() => setBulkAction('archive')}>Delete</BulkActionButton>}
        </BulkActionBar>
      )}

      {(bulkAction === 'addTags' || bulkAction === 'removeTags') && (
        <BulkTagsModal
          action={bulkAction}
          count={selected.size}
          busy={bulkBusy}
          onClose={() => setBulkAction(null)}
          onSubmit={(tagIds) => void runBulk({ action: bulkAction, params: { tagIds: [...tagIds] } })}
        />
      )}

      {bulkAction === 'setOwner' && (
        <BulkSetOwnerModal
          count={selected.size}
          busy={bulkBusy}
          onClose={() => setBulkAction(null)}
          onSubmit={(ownerId) => void runBulk({ action: 'setOwner', params: { ownerId } })}
        />
      )}

      {bulkAction === 'archive' && (
        <ConfirmModal
          title="Delete clients"
          message={`Delete ${selected.size} selected client(s)? They are soft-deleted — restorable from Admin → Trash.`}
          onClose={() => setBulkAction(null)}
          onConfirm={() => void runBulk({ action: 'archive' })}
          error={null}
          submitting={bulkBusy}
          confirmText="Delete"
          confirmClass="bg-rose-700 hover:bg-rose-600 text-oncolor"
        />
      )}

      {bulkResult && <BulkResultToast result={bulkResult} onClose={() => setBulkResult(null)} />}
    </div>
  );
}

export function ConfirmModal({ title, message, onClose, onConfirm, error, submitting, confirmText, confirmClass, confirmDisabled, children }: {
  title: string;
  message: string;
  onClose: () => void;
  onConfirm: () => void;
  error: string | null;
  submitting: boolean;
  confirmText: string;
  confirmClass: string;
  confirmDisabled?: boolean;
  children?: React.ReactNode;
}) {
  useEscape(onClose);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto mx-4 shadow-2xl animate-pop p-5">
        <h2 className="text-base font-semibold text-white mb-2">{title}</h2>
        <p className="text-sm text-muted mb-4">{message}</p>
        {children}
        {error && <p className="text-xs text-rose-400 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button onClick={onConfirm} disabled={submitting || confirmDisabled} className={`text-sm font-medium px-4 py-1.5 rounded-lg disabled:opacity-50 ${confirmClass}`}>
            {submitting ? 'Working…' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEscape(onClose);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-md max-h-[90vh] flex flex-col mx-4 shadow-2xl animate-pop">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="text-muted hover:text-white text-sm">✕</button>
        </div>
        <div className="px-5 py-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-muted mb-1">{label}{required && <span className="text-rose-400"> *</span>}</label>
      {children}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const colour = status === 'ACTIVE'
    ? 'pill-success'
    : status === 'LEAD'
    ? 'pill-warn'
    : status === 'INACTIVE'
    ? 'pill-neutral'
    : status === 'CHURNED'
    ? 'pill-danger'
    : 'pill-neutral';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs ${colour}`}>{status}</span>;
}


function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];
    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        row.push(cell);
        cell = '';
      } else if (ch === '\n') {
        row.push(cell);
        rows.push(row);
        row = [];
        cell = '';
      } else if (ch !== '\r') {
        cell += ch;
      }
    }
  }
  if (cell || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

const CLIENT_IMPORT_HEADERS = ['name', 'company', 'email', 'phone', 'country', 'industry', 'website', 'source', 'status', 'address', 'notes', 'ownerEmail'];

function downloadTemplate(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/\s+/g, '').replace(/[_-]/g, '');
}

function mapHeader(raw: string): string | null {
  const norm = normalizeHeader(raw);
  const aliases: Record<string, string> = {
    name: 'name',
    fullname: 'name',
    clientname: 'name',
    company: 'company',
    organization: 'company',
    org: 'company',
    email: 'email',
    emailaddress: 'email',
    phone: 'phone',
    phonenumber: 'phone',
    mobile: 'phone',
    country: 'country',
    countrycode: 'country',
    industry: 'industry',
    sector: 'industry',
    website: 'website',
    url: 'website',
    source: 'source',
    leadsource: 'source',
    status: 'status',
    clientstatus: 'status',
    address: 'address',
    streetaddress: 'address',
    notes: 'notes',
    notesmarkdown: 'notes',
    remarks: 'notes',
    owneremail: 'ownerEmail',
    owner: 'ownerEmail',
    assigneeemail: 'ownerEmail',
  };
  return aliases[norm] ?? null;
}

function parseImportRows(text: string): Array<Record<string, string>> {
  const grid = parseCsv(text.trim());
  if (grid.length < 2) return [];
  const headers = (grid[0] ?? []).map((h) => mapHeader(h)).map((h, idx) => h ?? `__col${idx}`);
  return grid.slice(1).map((row) => {
    const out: Record<string, string> = {};
    headers.forEach((h, idx) => {
      if (!h.startsWith('__col')) {
        let val = row[idx]?.trim() ?? '';
        if (h === 'website' && val && !/^https?:\/\//i.test(val)) {
          val = `https://${val}`;
        }
        out[h] = val;
      }
    });
    return out;
  }).filter((r) => Object.values(r).some((v) => v.length > 0));
}

function ImportClientsModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<Array<Record<string, string>>>([]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<{ created: number; failed: number; errors: string[] } | null>(null);
  const [dragOver, setDragOver] = useState(false);

  function handleFile(f: File | null) {
    setFile(f);
    setRows([]);
    setResult(null);
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = String(e.target?.result ?? '');
      const parsed = parseImportRows(text);
      setRows(parsed);
    };
    reader.readAsText(f);
  }

  function buildPayload() {
    return rows.map((r) => ({
      name: r.name || null,
      company: r.company || null,
      email: r.email || null,
      phone: r.phone || null,
      country: r.country || null,
      industry: r.industry || null,
      website: r.website || null,
      source: r.source || null,
      status: r.status || 'LEAD',
      address: r.address || null,
      notesMarkdown: r.notes || null,
      ownerEmail: r.ownerEmail || null,
    })).filter((r) => r.name);
  }

  function previewErrors(): Array<{ row: number; message: string }> {
    const errs: Array<{ row: number; message: string }> = [];
    rows.forEach((r, idx) => {
      if (!r.name?.trim()) errs.push({ row: idx + 2, message: 'Name is required' });
      if (r.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email)) errs.push({ row: idx + 2, message: `Invalid email: ${r.email}` });
      if (r.status && !['LEAD', 'ACTIVE', 'INACTIVE', 'CHURNED'].includes(r.status.toUpperCase())) errs.push({ row: idx + 2, message: `Invalid status: ${r.status}` });
    });
    return errs.slice(0, 20);
  }

  async function run(dry = false) {
    setRunning(true);
    setResult(null);
    const payload = buildPayload();
    if (payload.length === 0) {
      setResult({ created: 0, failed: 0, errors: ['No valid rows to import.'] });
      setRunning(false);
      return;
    }
    try {
      const res = await fetch('/v1/clients/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
        },
        body: JSON.stringify({ rows: payload, dryRun: dry }),
      });
      const body = await res.json().catch(() => ({ ok: false, error: { message: res.statusText } }));
      if (!res.ok || !body.ok) {
        setResult({ created: 0, failed: payload.length, errors: [body.error?.message ?? 'Import failed'] });
      } else {
        const data = body.data as { created: number; failed: number; errors: Array<{ row: number; field?: string; message: string }> };
        setResult({
          created: data.created,
          failed: data.failed,
          errors: data.errors.map((e) => `Row ${e.row}${e.field ? ` (${e.field})` : ''}: ${e.message}`),
        });
        queryClient.invalidateQueries({ queryKey: ['clients'] });
        if (!dry && data.created > 0) onSuccess();
      }
    } catch (e) {
      setResult({ created: 0, failed: payload.length, errors: [e instanceof Error ? e.message : String(e)] });
    }
    setRunning(false);
  }

  const template = [CLIENT_IMPORT_HEADERS.join(','), 'Acme Corp,Acme Inc,contact@acme.com,+971501234567,AE,Technology,https://example.com,REFERRAL,ACTIVE,Dubai HQ,Key account,aqib@1financial.net'].join('\n');
  const errs = previewErrors();
  const payload = buildPayload();

  return (
    <ModalShell title="Import clients" onClose={onClose}>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted">Upload a CSV file with a header row (in Excel: File → Save As → “CSV UTF-8”). Required: name. Optional: company, email, phone, country, industry, website, source, status, address, notes, ownerEmail.</p>
          <button onClick={() => downloadTemplate('clients-import-template.csv', template)} className="text-xs btn-ghost-accent border px-2 py-1 rounded">Download template</button>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0] ?? null); }}
          className={`border-2 border-dashed rounded-lg px-4 py-6 text-center ${dragOver ? 'border-accent bg-emerald-900/20' : 'border-border'}`}
        >
          <input
            type="file"
            accept=".csv"
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            className="hidden"
            id="client-import-file"
          />
          <label htmlFor="client-import-file" className="cursor-pointer text-sm text-muted hover:text-white">
            {file ? file.name : 'Drop a CSV file here or click to browse'}
          </label>
        </div>

        {rows.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-muted">{rows.length} rows parsed · {payload.length} valid · {errs.length} preview errors</p>
            {errs.length > 0 && (
              <div className="tint-danger rounded-lg p-2 max-h-24 overflow-y-auto">
                {errs.map((e, i) => <p key={i} className="text-xs text-rose-300">Row {e.row}: {e.message}</p>)}
              </div>
            )}
            <div className="bg-bg-base border border-border rounded-lg overflow-hidden overflow-x-auto max-h-48">
              <table className="w-full text-xs">
                <thead className="bg-bg-card text-muted">
                  <tr>
                    {CLIENT_IMPORT_HEADERS.map((h) => <th key={h} className="px-2 py-1 text-left font-medium whitespace-nowrap">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 10).map((r, i) => (
                    <tr key={i} className="border-t border-border/50">
                      {CLIENT_IMPORT_HEADERS.map((h) => <td key={h} className="px-2 py-1 text-white whitespace-nowrap">{r[h] || '—'}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > 10 && <p className="text-xs text-muted px-2 py-1">…and {rows.length - 10} more</p>}
            </div>
          </div>
        )}

        {result && (
          <div className={`text-xs p-2 rounded-lg ${result.failed > 0 ? 'tint-danger' : 'tint-success'}`}>
            <p>Created {result.created}, failed {result.failed}</p>
            {result.errors.length > 0 && (
              <div className="max-h-24 overflow-y-auto mt-1 space-y-1">
                {result.errors.map((e, i) => <p key={i}>{e}</p>)}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button onClick={() => run(true)} disabled={running || payload.length === 0} className="text-sm btn-ghost-accent border px-3 py-1.5 rounded-lg disabled:opacity-50">{running ? 'Checking…' : 'Preview'}</button>
          <button onClick={() => run(false)} disabled={running || payload.length === 0} className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg">{running ? 'Importing…' : 'Import'}</button>
        </div>
      </div>
    </ModalShell>
  );
}


function BulkTagsModal({ action, count, busy, onClose, onSubmit }: {
  action: 'addTags' | 'removeTags';
  count: number;
  busy: boolean;
  onClose: () => void;
  onSubmit: (tagIds: Set<string>) => void;
}) {
  const [tagIds, setTagIds] = useState<Set<string>>(new Set());
  return (
    <ModalShell
      title={action === 'addTags' ? `Add tags to ${count} client(s)` : `Remove tags from ${count} client(s)`}
      onClose={onClose}
    >
      <div className="space-y-3">
        <TagCheckboxes selected={tagIds} onChange={setTagIds} />
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button
            onClick={() => onSubmit(tagIds)}
            disabled={busy || tagIds.size === 0}
            className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
          >
            {busy ? 'Applying…' : action === 'addTags' ? 'Add tags' : 'Remove tags'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function BulkSetOwnerModal({ count, busy, onClose, onSubmit }: {
  count: number;
  busy: boolean;
  onClose: () => void;
  onSubmit: (ownerId: string | null) => void;
}) {
  const [ownerId, setOwnerId] = useState<string | null | undefined>(undefined);
  return (
    <ModalShell title={`Set owner for ${count} client(s)`} onClose={onClose}>
      <div className="space-y-3">
        <label className="block text-xs text-muted">New owner</label>
        <StaffSelect value={ownerId} onChange={setOwnerId} mode="required" />
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button
            onClick={() => onSubmit(ownerId ?? null)}
            disabled={busy || ownerId === undefined}
            className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
          >
            {busy ? 'Applying…' : 'Set owner'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
